import { ChangeEvent, DragEvent, DragEventHandler, FC, memo, useCallback, useRef, useState } from 'react'

import { AttachFile } from '@mui/icons-material'
import { Button, Card, Dialog, Typography, useTheme } from '@mui/material'
import { Stack } from '@mui/system'

import { useTranslation } from 'react-i18next'

import { useMentorCloud } from 'src/contexts/MentorCloudContext'

import { useVideoUpload } from 'src/hooks/files/useVideoUpload'
import useFileMutation from 'src/hooks/useFileMutation'
import useToast from 'src/hooks/useToast'

import { generateId } from 'src/utils/random'

import authService from 'src/service/auth-service'

interface MentorCloudUploaderProps {
  isOpen: boolean
  onClose: () => void
  parentFolderId?: number | null
}

export const MentorCloudUploaderModal: FC<MentorCloudUploaderProps> = memo(props => {
  const { isOpen, onClose, parentFolderId } = props

  const { notify } = useToast()
  const { t } = useTranslation()
  const theme = useTheme()

  const { onChangeUploadFiles, onUploadFilesChangeProgress } = useMentorCloud()

  const fileRef = useRef<HTMLInputElement | null>(null)

  const [isDragging, setIsDragging] = useState<boolean>(false)

  const onDrag = useCallback(
    (isDragging: boolean): DragEventHandler<HTMLDivElement> =>
      e => {
        e.preventDefault()
        setIsDragging(isDragging)
      },
    []
  )

  const { uploadFile } = useFileMutation()

  const { uploadVideo: uploadVideo, convertVideo } = useVideoUpload()

  const uploadAndConvertVideo = async (file: File) => {
    if (!file) {
      return
    }

    if (!file.type.includes('video')) {
      notify(t('FILE_TYPE.video'), { type: 'error' })

      return
    }

    const formData = new FormData()
    formData.append('payload', file)
    formData.append('name', file.name)

    if (parentFolderId) {
      formData.append('folder_id', parentFolderId.toString())
    }
    const token = authService.getUserToken()
    if (token) {
      formData.append('token', token)
    }

    const source = await uploadVideo({
      formData,
      onProgress: progress => onUploadFilesChangeProgress?.(file, progress)
    })

    await convertVideo({ source, once: true })
    onChangeUploadFiles?.(prev => prev.filter(uploadedFile => uploadedFile.file.name !== file.name))
  }

  const upload = async (files: File[]) => {
    const filesToUpload: Promise<any>[] = []
    files.forEach(file => {
      if (file.type.includes('video')) {
        onChangeUploadFiles?.(prev => [...prev, { file, progress: 0, convertProgress: 0, parentFolderId }])
        filesToUpload.push(uploadAndConvertVideo(file))

        return
      }

      onChangeUploadFiles?.(prev => [...prev, { file, progress: 0, parentFolderId }])

      const fileUpload = async () => {
        await uploadFile({
          file,
          id: generateId(),
          parentFolderId,
          onProgress: progress => onUploadFilesChangeProgress?.(file, progress)
        })

        onChangeUploadFiles?.(prev => prev.filter(uploadedFile => uploadedFile.file.name !== file.name))
      }
      filesToUpload.push(fileUpload())
    })

    await Promise.all(filesToUpload)
    onChangeUploadFiles?.([])
  }

  const onUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return

    onClose()
    await upload(Array.from(e.target.files))
  }

  const onDrop = async (e: DragEvent<HTMLDivElement>) => {
    onDrag(false)(e)

    onClose()
    await upload(Array.from(e.dataTransfer.files))
  }

  return (
    <Dialog
      maxWidth='md'
      fullWidth
      PaperProps={{
        sx: { overflow: 'visible', position: 'relative' }
      }}
      open={isOpen}
      onClose={onClose}
    >
      <input multiple type='file' id='file' ref={fileRef} onChange={onUpload} style={{ display: 'none' }} />

      <Card>
        <Stack padding={theme.spacing(4)}>
          <Stack
            padding={theme.spacing(8, 4)}
            borderRadius={theme.spacing(1.5)}
            border={`1px dashed ${isDragging ? theme.palette.primary[500] : theme.palette.divider}`}
            justifyContent={'center'}
            gap={theme.spacing(2)}
            alignItems={'center'}
            onDrop={onDrop}
            onDragOver={onDrag(true)}
            onDragLeave={onDrag(false)}
          >
            <Stack padding={theme.spacing(2)} borderRadius={theme.spacing(1.5)} bgcolor={theme.palette.grey[100]}>
              <AttachFile color='secondary' />
            </Stack>
            <Typography fontWeight={400} variant='h5' align='center'>
              {t('ACTION.ENTITY.drag_drop_files')}
            </Typography>
            <Typography color={theme.palette.text.disabled} variant='body1'>
              {t('MISC.or')}
            </Typography>
            <Button onClick={() => fileRef.current?.click()} variant='contained'>
              {t('ACTION.ENTITY.browse_entities', { entities: t('FILE_TYPE.files') })}
            </Button>
          </Stack>
        </Stack>
      </Card>
    </Dialog>
  )
})
